import type { TFunction } from 'i18next';

import { validateWildcardPrefix, type PathErrorCode } from './seo-validation';
import type { SiteSeoGenerateField, SiteSeoJob, SiteSeoRoute } from './seo-types';

/**
 * The SEO page's computed copy: one literal `t()` key per case, so the extractor harvests every one; each `switch`
 * is exhaustive, so a new code fails the typecheck until it has its copy.
 */

/** The Add route dialog's refusal of `path` (`path-format` told apart by what is wrong with it). */
export const pathErrorCopy = (t: TFunction, code: PathErrorCode, path: string): string => {
  switch (code) {
    case 'path-format':
      if (!path.startsWith('/')) return t('Start with /');
      if (path.includes('*')) return t('Use * only at the end, as /prefix/*');
      return t("A route can't contain ? or #");
    case 'path-pattern':
      return t('That is a dynamic page: pick it from Dynamic routes');
    case 'path-site-page':
      return t('That is a site page: pick it from Site pages');
    case 'path-duplicate':
      return t('That route is already in the list');
    case 'path-dynamic-wildcard':
      return t('That group already belongs to a dynamic page');
    case 'path-root-wildcard':
      return t('Site defaults already cover every page');
    case 'path-no-page':
      return t('No page on your site starts with {{prefix}}', { prefix: validateWildcardPrefix(path) ?? path });
  }
};

export type RouteKind = 'site-page' | 'dynamic-page' | 'single-page' | 'footer-wildcard';

/** A route's kind, from its match and origin: a pattern is always a dynamic page (owners cannot add one). */
export const routeKindOf = (route: Pick<SiteSeoRoute, 'match' | 'origin'>): RouteKind => {
  if (route.match === 'wildcard') return 'footer-wildcard';
  if (route.match === 'pattern') return 'dynamic-page';
  return route.origin === 'site_page' ? 'site-page' : 'single-page';
};

export const routeKindCopy = (t: TFunction, kind: RouteKind): string => {
  switch (kind) {
    case 'site-page':
      return t('Site page');
    case 'dynamic-page':
      return t('Dynamic page');
    case 'single-page':
      return t('Single page');
    case 'footer-wildcard':
      return t('Footer wildcard');
  }
};

/** The publish confirm's summary; either count may be one, and a change of no text names no language. */
export const publishSummaryCopy = (t: TFunction, n: number, m: number): string => {
  if (m === 0) return n === 1 ? t('1 page changed') : t('{{n}} pages changed', { n });
  if (n === 1) return m === 1 ? t('1 page and 1 language changed') : t('1 page and {{m}} languages changed', { m });
  return m === 1
    ? t('{{n}} pages and 1 language changed', { n })
    : t('{{n}} pages and {{m}} languages changed', { n, m });
};

/** The header's note while a job runs: an undo, or a publish with its phase in words (plain for a phase it does not
 *  know, so a new server phase never shows its raw code). */
export const runningJobCopy = (t: TFunction, job: SiteSeoJob): string => {
  if (job.action === 'revert') return t('Undoing…');
  switch (job.phase) {
    case 'footer':
      return t('Publishing… updating the footer');
    case 'template':
      return t('Publishing… writing the page settings');
    case 'deploy':
      return t('Publishing… redeploying the site');
    case 'verify':
      return t('Publishing… checking the site');
    case 'finish':
      return t('Publishing… finishing');
    case 'restore':
      return t('Publishing… putting the previous values back');
    default:
      return t('Publishing…');
  }
};

/** A site field as its panel names it. */
const generateFieldCopy = (t: TFunction, field: SiteSeoGenerateField): string => {
  switch (field) {
    case 'title':
      return t('Meta title');
    case 'description':
      return t('Meta description');
    case 'footer_text':
      return t('Footer SEO text');
    case 'llms_summary':
      return t('Summary for AI assistants');
    case 'llms_facts':
      return t('Key facts');
  }
};

/** Generate site defaults: the empty fields Lumio writes in one language, counted and named in the order asked. */
export const defaultsFieldsCopy = (t: TFunction, fields: readonly SiteSeoGenerateField[]): string => {
  const names = fields.map((field) => generateFieldCopy(t, field)).join(', ');
  return fields.length === 1
    ? t('1 empty field: {{fields}}', { fields: names })
    : t('{{n}} empty fields: {{fields}}', { n: fields.length, fields: names });
};

/** Generate site defaults: how many pages inherit the new description and how many keep their own ('' with no pages;
 *  the keep part only when some page keeps its own). */
export const defaultsPagesCopy = (t: TFunction, usingDefault: number, keepingOwn: number): string => {
  if (usingDefault === 0 && keepingOwn === 0) return '';
  const using =
    usingDefault === 0
      ? t('No page will use the new default description')
      : usingDefault === 1
        ? t('1 page will use the new default description')
        : t('{{n}} pages will use the new default description', { n: usingDefault });
  if (keepingOwn === 0) return using;
  const keeping = keepingOwn === 1 ? t('1 keeps its own text') : t('{{m}} keep their own text', { m: keepingOwn });
  return `${using} · ${keeping}`;
};

/** The name of the field an issue is about, as its panel labels it ('' for a field no panel names). */
export const issueFieldCopy = (t: TFunction, field: string | undefined, siteLevel: boolean): string => {
  switch (field) {
    case 'title':
      return t('Meta title');
    case 'description':
      return siteLevel ? t('Default description') : t('Meta description');
    case 'title_template':
      return t('Title template');
    case 'footer_text':
      return t('Footer SEO text');
    case 'share_image':
      return t('Share image');
    case 'canonical':
      return t('Canonical URL');
    case 'noindex':
      return t('Hide from Google (noindex)');
    case 'llms_summary':
      return t('Summary for AI assistants');
    case 'llms_facts':
      return t('Key facts');
    case 'path':
      return t('Address');
    default:
      return '';
  }
};
